import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, Upload, Avatar, App, Divider, Alert } from 'antd';
import { UploadOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { getUserById, updateUserProfile, uploadAvatar } from '../../services/users';
import { resolveMedia } from '../../utils/media';
import { getRoleLabel } from '../../utils/constants';

const { useApp } = App;

const alphaRegex = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]+$/;
const phoneRegex = /^\d{10}$/;

const UserProfile = () => {
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(undefined);
  const [originalPhotoUrl, setOriginalPhotoUrl] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Obtener el ID del usuario desde localStorage, contexto de autenticación, o props
  const getCurrentUserId = () => {
    // Aquí debes implementar la lógica para obtener el ID del usuario actual
    // Por ejemplo, desde localStorage, context de autenticación, o token decodificado
    try {
      // Opción 1: Desde localStorage (si guardas user data allí)
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user._id;
      }
      
      // Opción 2: Desde sessionStorage
      const sessionUser = sessionStorage.getItem('user');
      if (sessionUser) {
        const user = JSON.parse(sessionUser);
        return user.id || user._id;
      }
      
      // Opción 3: Si usas token JWT, podrías decodificarlo
      // const token = localStorage.getItem('token');
      // if (token) {
      //   const decoded = jwtDecode(token);
      //   return decoded.userId || decoded.sub;
      // }
      
      return null;
    } catch (error) {
      console.error('Error obteniendo ID de usuario:', error);
      return null;
    }
  };

  const currentUserId = getCurrentUserId();

  useEffect(() => {
    if (currentUserId) {
      loadUserData();
    } else {
      message.error('No se pudo identificar al usuario. Por favor, inicia sesión nuevamente.');
      navigate('/login');
    }
  }, [currentUserId, navigate, message]);

  const loadUserData = () => {
    if (!currentUserId) return;
    
    setLoading(true);
    getUserById(currentUserId)
      .then((response) => {
        const user = response.data;
        setUserData(user);
        
        form.setFieldsValue({
          firstName: user.firstName,
          lastName: user.lastName,
          secondLastName: user.secondLastName || undefined,
          email: user.email,
          phone: user.phone?.replace('+52', '') || undefined,
        });
        
        if (user.photoUrl) {
          setOriginalPhotoUrl(user.photoUrl);
          setAvatarPreview(resolveMedia(user.photoUrl));
        }
      })
      .catch((err) => {
        console.error('Error cargando datos del usuario:', err);
        message.error('No se pudo cargar la información del usuario');
        
        // Si hay error 401 o 403, redirigir al login
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          message.warning('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
          navigate('/login');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const uploadProps = useMemo(
    () => ({
      maxCount: 1,
      beforeUpload: (file) => {
        const isImg = file.type.startsWith('image/');
        if (!isImg) {
          message.error('Solo se permiten imágenes (jpg, png, webp, etc.)');
          return Upload.LIST_IGNORE;
        }

        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
          message.error('La imagen debe pesar menos de 2MB');
          return Upload.LIST_IGNORE;
        }

        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
        return false;
      },
      onRemove: () => {
        setAvatarFile(null);
        setAvatarPreview(undefined);
      },
      showUploadList: false,
    }),
    [message]
  );

  /**
   * Función para actualizar los datos del perfil
   */
  const onUpdateProfile = async (values) => {
    try {
      if (!currentUserId) {
        message.error('Usuario no identificado');
        return;
      }

      setLoading(true);

      let photoUrlToSend = undefined;
      
      if (avatarFile) {
        const uploadResult = await uploadAvatar(avatarFile);
        photoUrlToSend = uploadResult.url.avatarUrl || uploadResult;
      } else if (originalPhotoUrl) {
        photoUrlToSend = originalPhotoUrl;
      }

      const updateData = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        secondLastName: values.secondLastName?.trim() || undefined,
        phone: '+52'+values.phone || null,
        ...(photoUrlToSend !== undefined && { photoUrl: photoUrlToSend }),
      };

    await updateUserProfile(currentUserId, updateData);
      
      message.success('Perfil actualizado correctamente');
      loadUserData(); // Recargar datos
    } catch (err) {
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al actualizar el perfil';
      
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        if (issues.length > 0) {
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message)
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Función para cambiar la contraseña
   */
  const onChangePassword = async (values) => {
    try {
      if (!currentUserId) {
        message.error('Usuario no identificado');
        return;
      }

      setPasswordLoading(true);

      const passwordData = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      };

      // Actualizar la contraseña usando updateUserProfile o función específica
      await updateUserProfile(currentUserId,passwordData); // Esta función debe manejar cambio de contraseña
      
      message.success('Contraseña actualizada correctamente');
      
      // Resetear formulario de contraseña
      passwordForm.resetFields();
      setShowPasswordForm(false);
    } catch (err) {
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al cambiar la contraseña';
      
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        if (issues.length > 0) {
          passwordForm.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message)
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          if (fieldErrors.length > 0) {
            passwordForm.setFields(fieldErrors);
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else if (status === 401) {
        message.error('Contraseña actual incorrecta');
        passwordForm.setFields([{
          name: 'currentPassword',
          errors: ['La contraseña actual es incorrecta'],
        }]);
      } else {
        message.error(errorMessage);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!currentUserId) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Card>
          <p>No se pudo cargar la información del usuario.</p>
          <Button type="primary" onClick={() => navigate('/login')}>
            Ir al login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <Card title="Mi Perfil" loading={loading && !showPasswordForm}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onUpdateProfile}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Foto de perfil"
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <Avatar
                    size={128}
                    src={avatarPreview}
                    icon={<UserOutlined />}
                  />
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Cambiar imagen</Button>
                  </Upload>
                </div>
              </Form.Item>
            </Col>
            
            <Col xs={24} md={16}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Nombre"
                    name="firstName"
                    rules={[
                      { required: true, message: 'El nombre es requerido' },
                      { pattern: alphaRegex, message: 'Solo se permiten letras y espacios' },
                    ]}
                  >
                    <Input placeholder="Nombres" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Apellidos"
                    name="lastName"
                    rules={[
                      { required: true, message: 'El apellido es requerido' },
                      { pattern: alphaRegex, message: 'Solo se permiten letras y espacios' },
                    ]}
                  >
                    <Input placeholder="Apellidos" allowClear />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Correo electrónico"
                name="email"
              >
                <Input
                  placeholder="contacto@ejemplo.com"
                  allowClear
                  disabled
                  suffix={userData?.emailVerified ? 
                    <span style={{ color: '#52c41a', fontSize: '12px' }}>✓ Verificado</span> : 
                    <span style={{ color: '#faad14', fontSize: '12px' }}>No verificado</span>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Teléfono móvil"
                name="phone"
                rules={[
                  { pattern: phoneRegex, message: 'Debe tener exactamente 10 dígitos' },
                ]}
              >
                <Input
                  placeholder="0000000000"
                  maxLength={10}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, '');
                    form.setFieldsValue({ phone: onlyDigits });
                  }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label="Rol"
              >
                <Input
                  value={userData?.role ? getRoleLabel(userData.role) : 'No asignado'}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                Guardar cambios
              </Button>
            </Col>
          </Row>
        </Form>

        <Divider>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LockOutlined />
            <span>Cambiar contraseña</span>
          </div>
        </Divider>

        {!showPasswordForm ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Button 
              type="default" 
              onClick={() => setShowPasswordForm(true)}
              icon={<LockOutlined />}
            >
              Cambiar contraseña
            </Button>
          </div>
        ) : (
          <Card size="small" bordered={false} style={{ backgroundColor: '#fafafa' }}>
            <Alert
              message="Cambio de contraseña"
              description="Ingresa tu contraseña actual y la nueva contraseña"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={onChangePassword}
              autoComplete="off"
            >
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    label="Contraseña actual"
                    name="currentPassword"
                    rules={[
                      { required: true, message: 'La contraseña actual es requerida' },
                      { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' },
                    ]}
                  >
                    <Input.Password
                      placeholder="Ingresa tu contraseña actual"
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Nueva contraseña"
                    name="newPassword"
                    rules={[
                      { required: true, message: 'La nueva contraseña es requerida' },
                      { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' },
                    ]}
                  >
                    <Input.Password
                      placeholder="Ingresa la nueva contraseña"
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Confirmar nueva contraseña"
                    name="confirmNewPassword"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: 'Confirma la nueva contraseña' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Las contraseñas no coinciden'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      placeholder="Confirma la nueva contraseña"
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="end" gutter={12} style={{ marginTop: 16 }}>
                <Col>
                  <Button 
                    onClick={() => {
                      setShowPasswordForm(false);
                      passwordForm.resetFields();
                    }}
                    disabled={passwordLoading}
                  >
                    Cancelar
                  </Button>
                </Col>
                <Col>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={passwordLoading}
                  >
                    Cambiar contraseña
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default UserProfile;